import React from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { getByPath } from '../dataPaths';
import { SelectReportData, selectSectionStartPages } from '../Selector';

// Outline entries are listed in this fixed order; each one mirrors the live
// `sectionHeading` of the page it points to, so editing a page heading keeps
// the outline in sync. Page numbers are resolved at render time from the
// running page count, so multi-page sections shift the rest accordingly.
const OUTLINE_SECTION_SOURCES = [
  'circumplexData',
  'topStrengths',
  'strenghtComments',
  'keyDevelopmentAreas',
  'devlopmentAreasComments',
  'twoByTwoMatrix',
  'wordsOfWisdom',
  'ratingsOnEachBehavior',
];

const TITLE_CASE_MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'of', 'on', 'or',
  'the', 'to', 'vs', 'via',
]);

function stripHtml(html) {
  if (html == null) return '';
  return String(html).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function toTitleCase(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word, i) =>
      i !== 0 && TITLE_CASE_MINOR_WORDS.has(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
}

function OutlinePage({ data, personName, sectionHeadings, sectionPages, pageNumber }) {
  const entries = Object.entries(data.content || {});

  return (
    <PageWrapper pageNumber={pageNumber} personName={personName}>
      <EditableText
        as="h2"
        className="pdf-section-heading"
        path="outlineOfThisReport.sectionHeading"
        variant="inline"
        stripInlineWrap
      />

      <div className="outline-list">
        {entries.map(([key, description], idx) => {
          const heading = sectionHeadings[idx];
          const title = heading ? toTitleCase(heading) : key;
          const page = sectionPages[idx];
          return (
            <div key={key} className="outline-item">
              <div className="outline-item__badge">{idx + 1}</div>
              <div className="outline-item__content">
                <h4 className="outline-item__title">{title}</h4>
                <EditableText
                  as="p"
                  className="outline-item__desc"
                  path={`outlineOfThisReport.content[${key}]`}
                  variant="inline"
                  stripInlineWrap
                  fallback={description}
                />
              </div>
              {page != null && (
                <div className="outline-item__page">Page {page}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="outline-how-to-use">
        <EditableText
          as="h4"
          className="outline-how-to-use__heading"
          path="outlineOfThisReport.howToUseHeading"
          variant="inline"
          stripInlineWrap
        />
        <EditableText
          as="div"
          className="outline-how-to-use__list-wrap"
          path="outlineOfThisReport.howToUseTips"
          variant="block"
        />
      </div>
    </PageWrapper>
  );
}

const mapStateToProps = (state) => {
  const reportData = SelectReportData(state);
  const startPages = selectSectionStartPages(state);
  const sectionHeadings = OUTLINE_SECTION_SOURCES.map((source) =>
    stripHtml(getByPath(reportData, `${source}.sectionHeading`))
  );
  const sectionPages = OUTLINE_SECTION_SOURCES.map((source) => startPages[source]);
  return { sectionHeadings, sectionPages };
};

export default connect(mapStateToProps)(OutlinePage);
